import {
  Box,
  BoxProps,
  ColorOptions,
  Icon,
  IconOptions,
  RadioButton,
  RadioButtonProps,
  Spacer,
  Text,
  TextProps
} from '@jfs/ui-new'

import { useCallback } from 'react'
import { MerchantTheme } from '../../../data/models/Config'
import { useStores } from '../../../data/store'
import { EMI_TYPES } from '../../../utils/constants'
import { convertHexToRGBA } from '../../../utils/index'

export interface ListItemProps {
  bankId?: string
  bankName?: string
  emiPlans?: any
  title?: string
  type?: string
  description?: string
  icon?: IconOptions
  planSource?: string
}

export interface ListDataProps {
  item: ListItemProps
  index: number
  section: {
    headerTitle: string
    data: ListItemProps[]
  }
}

interface ItemProps {
  data: ListDataProps
  checkedObject: ListItemProps
  TYPE?: string
  onRadioButtonClick: (item: ListItemProps, index: number) => void
  onViewPlanPress: () => void
}

export const ListItem = ({
  data,
  checkedObject,
  onRadioButtonClick,
  TYPE = 'PAYMENT_OPTION_LIST',
  onViewPlanPress,
}: ItemProps) => {
  const { theme } = useStores((root) => ({
    theme: root?.config?.merchantTheme,
  }))

  const onChange = useCallback(() => onRadioButtonClick(data.item, data?.index), [data, onRadioButtonClick])

  const isChecked = useCallback(
    () => Object.entries(checkedObject).sort().toString() === Object.entries(data.item).sort().toString(),
    [checkedObject, data.item],
  )

  const getDescriptionText = useCallback(() => {
    const emiPlan = data.item?.emiPlans?.[0]
    return (
      emiPlan &&
      (emiPlan.allowZeroCost === EMI_TYPES.NO_COST
        ? `No Cost EMI at ₹${emiPlan.value}/month`
        : `EMI Starting from ₹${emiPlan.value}/month`)
    )
  }, [data.item])

  return (
    <Box {...styles.container} onPress={onChange} testID={data.item.type}>
      <RadioButton {...styles.radioButton(theme as any)} checked={isChecked()} onChange={onChange} />
      <Box {...styles.innerContainer(theme as any)}>
        <Icon {...styles.icon(theme as any)} id={data.item.icon} />
        <Spacer direction="horizontal" size={22} />
        {TYPE === 'PAYMENT_OPTION_LIST' && (
          <Box flexDirection={'column'}>
            <Text {...styles.title(theme as any)}>{data.item.title}</Text>
            <Spacer direction="vertical" size={6} />
            <Box flexDirection={'row'}>
              <Text {...styles.description(theme as any)}>{data.item.description}</Text>
              {data.item.type === 'credit_card' && (
                <Box
                  borderBottomWidth={0.5}
                  borderBottomColor={theme.brandColor as any}
                  ml={8}
                  underlayColor={'TRANSPARENT'}
                  testID="view-plans"
                  onPress={onViewPlanPress}>
                  <Text {...styles.viewPlan(theme as any)}>View Plans</Text>
                </Box>
              )}
            </Box>
            <Spacer direction="vertical" size={4} />
          </Box>
        )}
        {TYPE === 'BANK_LIST' && (
          <Box flexDirection={'column'}>
            <Text {...styles.title(theme as any)}>{data.item.bankName}</Text>
            <Spacer direction="vertical" size={6} />
            {data.item.planSource !== '2' && <Text {...styles.description(theme as any)}>{getDescriptionText()}</Text>}
            <Spacer direction="vertical" size={4} />
            <Text {...styles.extraInfo}>
              {!data.item.emiPlans && '*One more step to complete your EMI eligibility.'}
            </Text>
          </Box>
        )}
      </Box>
    </Box>
  )
}

const styles = {
  container: {
    flexDirection: 'row',
    pl: 16,
    underlayColor: 'TRANSPARENT',
  } as BoxProps,
  radioButton: (theme: typeof MerchantTheme.properties) =>
  ({
    size: 16,
    color: theme?.brandColor as any,
    hoverColor: 'TRANSPARENT',
    unCheckedColor: convertHexToRGBA(theme?.bodyTextColor, 30),
  } as RadioButtonProps),
  innerContainer: (theme: typeof MerchantTheme.properties) =>
  ({
    flexDirection: 'row',
    borderBottomWidth: 0.5,
    alignItems: 'center',
    p: 16,
    flex: 1,
    borderBottomColor: convertHexToRGBA(theme?.bodyTextColor, 10) as ColorOptions,
  } as BoxProps),
  icon: (theme: typeof MerchantTheme.properties) => ({
    size: 20,
    color: theme?.brandColor as any,
  }),
  title: (theme: typeof MerchantTheme.properties) =>
  ({
    color: theme.bodyTextColor as any,
    fontSize: 14,
    fontFamily: 'jioType-medium',
  } as TextProps),
  description: (theme: typeof MerchantTheme.properties) =>
  ({
    color: convertHexToRGBA(theme?.bodyTextColor, 50),
    fontSize: 12,
    fontFamily: 'jioType-light',
  } as TextProps),
  viewPlan: (theme: typeof MerchantTheme.properties) =>
  ({
    color: theme?.brandColor as any,
    fontSize: 12,
    fontFamily: 'jioType-light',
    textDecorationStyle: 'solid',
  } as TextProps),
  extraInfo: {
    color: '#E58B10' as any,
    fontSize: 10,
    fontFamily: 'jioType-medium',
    fontStyle: 'italic',
  } as TextProps,
}
